import { useState, useCallback, useRef, useEffect } from 'react'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'

/**
 * 儲位搜尋 Hook
 * 
 * @param {Object} shelfService - Shelf Service
 * @param {Object} options - 配置選項
 */
export default function useShelfSearch(shelfService, options = {}) {
  const { t } = useTranslation()
  const {
    minSearchLength = 2,
    limit = 50,
    debounceDelay = 300,
  } = options

  const [shelves, setShelves] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [inputValue, setInputValue] = useState('')
  const debounceTimer = useRef(null)

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  const performSearch = useCallback(async (searchText) => {
    if (!searchText || searchText.length < minSearchLength) {
      setShelves([])
      setSearchKeyword('')
      return
    }

    setLoading(true)
    setSearchKeyword(searchText)
    try {
      // 這裡使用 index (fetchList) 進行搜尋
      const response = await shelfService.getRecords({
        search: searchText,
        page_size: limit,
        page: 1
      })

      if (response.success) {
        const shelfOptions = (response.data || []).map(shelf => ({
          value: shelf.id,
          label: shelf.location,
          location: shelf.location,
          ...shelf
        }))
        setShelves(shelfOptions)
      } else {
        setShelves([])
      }
    } catch (error) {
      console.error('[useShelfSearch] Search failed:', error)
      message.error(t('common.error.fetch_failed'))
      setShelves([])
    } finally {
      setLoading(false)
    }
  }, [shelfService, minSearchLength, limit, t])

  const searchShelves = useCallback((searchText) => {
    setInputValue(searchText || '')

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    if (!searchText || searchText.length < minSearchLength) {
      setShelves([])
      setSearchKeyword('')
      setLoading(false)
      return
    }

    setLoading(true)
    debounceTimer.current = setTimeout(() => {
      performSearch(searchText)
    }, debounceDelay)
  }, [performSearch, debounceDelay, minSearchLength])

  const clearShelves = useCallback(() => {
    setShelves([])
    setSearchKeyword('')
    setInputValue('')
  }, [])

  return {
    shelves,
    loading,
    searchKeyword,
    inputValue,
    minSearchLength,
    searchShelves,
    clearShelves
  }
}
